import { Stack, useTheme } from "@mui/material";
import { Button, FoodsTextInput, Typography } from "@foodpilot/foods";
import { FacetValueRow } from "./FacetValueRow";
import { filterByLabel } from "./facetLabelFilter";
import { isFacetValueChecked, selectOnlyFacetValue, toggleFacetValue } from "./facetSelection";
import { FACET_VALUE_FILTER_THRESHOLD, sliceVisibleFacetValues } from "./facetVisibility";
import type { FacetPanelLabels, FacetSelection, FacetValue } from "./types";

type FacetValueListProps = {
    id?: string;
    values: FacetValue[];
    domain?: string[];
    selection: FacetSelection;
    labels: FacetPanelLabels;
    showAllValues: boolean;
    onToggleShowAllValues: () => void;
    valueFilterQuery: string;
    onValueFilterQueryChange: (query: string) => void;
    onSelectionChange: (selection: FacetSelection) => void;
};

export const FacetValueList = (props: FacetValueListProps) => {
    const {
        id,
        values,
        domain,
        selection,
        labels,
        showAllValues,
        onToggleShowAllValues,
        valueFilterQuery,
        onValueFilterQueryChange,
        onSelectionChange,
    } = props;
    const theme = useTheme();

    const filteredValues = filterByLabel(values, valueFilterQuery);
    const { visible, exceedsLimit } = sliceVisibleFacetValues(filteredValues, showAllValues);

    return (
        <Stack id={id} paddingLeft={theme.spacing(1.5)}>
            {values.length > FACET_VALUE_FILTER_THRESHOLD && (
                <FoodsTextInput
                    value={valueFilterQuery}
                    onChange={onValueFilterQueryChange}
                    ExtraInputProps={{
                        placeholder: labels.valueFilterPlaceholder,
                        "aria-label": labels.valueFilterPlaceholder,
                    }}
                />
            )}
            {filteredValues.length === 0 ?
                <Typography variant="small-regular" color="text.secondary">
                    {labels.valueFilterEmpty}
                </Typography>
            :   visible.map((value) => (
                    <FacetValueRow
                        key={value.value}
                        value={value}
                        isChecked={isFacetValueChecked(selection, value.value)}
                        onlyLabel={labels.only}
                        onlyActionLabel={labels.onlyValue(value.label)}
                        onToggle={() => onSelectionChange(toggleFacetValue(selection, value.value, domain))}
                        onSelectOnly={() => onSelectionChange(selectOnlyFacetValue(value.value, domain))}
                    />
                ))
            }
            {exceedsLimit && (
                <Button
                    variant="text"
                    size="small"
                    onClick={onToggleShowAllValues}
                    aria-expanded={showAllValues}
                    sx={{
                        alignSelf: "flex-start",
                        minWidth: "auto",
                        height: "auto",
                        padding: 0,
                        marginLeft: theme.spacing(0.5),
                        textDecoration: "underline",
                    }}
                >
                    <Typography variant="small-medium">{showAllValues ? labels.showLess : labels.showMore}</Typography>
                </Button>
            )}
        </Stack>
    );
};
